/** biome-ignore-all lint/suspicious/noTsIgnore: Ask @matejm for more information */
import { env } from '@/env/client';
import { LiveMicrophoneData } from '~/custom-plugins/live-microphone-data/dist/esm';
import type { AudioData } from '~/custom-plugins/live-microphone-data/dist/esm/definitions';
import type { ErrorStatus, SonioxError } from './errors';
import {
  isActiveState,
  isInactiveState,
  isWebSocketState,
  type RecorderState,
} from './state';
import type {
  Context,
  SpeechToTextAPIRequest,
  SpeechToTextAPIResponse,
} from './types';

const defaultWebsocketUri = 'wss://stt-rt.soniox.com/transcribe-websocket';

const defaultBufferQueueSize = 1000;

const finalizeMessage = '{ "type": "finalize" }';

type Callbacks = {
  onStateChange?: (update: {
    oldState: RecorderState;
    newState: RecorderState;
  }) => void;
  onStarted?: () => void;
  onPartialResult?: (result: SpeechToTextAPIResponse) => void;
  onFinished?: () => void;
  /**
   * Called when an error occurs.
   *
   * @param status - The error status.
   * @param message - More descriptive error message.
   * @param errorCode - The error code. Returned only if status is `api_error`.
   */
  onError?: (error: SonioxError) => void;
  onAudioPayload?: (payload: ArrayBuffer, timestamp: number) => void;
};

type TranscriptionConfigurationGetter = (options?: {
  forceRefresh?: boolean;
}) => Promise<{
  apiKey: string;
  websocketUri: string;
}>;

type ContextGetter = () => Promise<Context>;

const isLikelyAuthApiError = (
  errorCode: number | null | undefined,
  errorMessage: string | null | undefined,
): boolean => {
  if (errorCode === 401 || errorCode === 403) {
    return true;
  }

  if (errorMessage == null) {
    return false;
  }

  const normalizedMessage = errorMessage.toLowerCase();
  return (
    normalizedMessage.includes('api key') ||
    normalizedMessage.includes('token') ||
    normalizedMessage.includes('expired') ||
    normalizedMessage.includes('unauthorized') ||
    normalizedMessage.includes('forbidden') ||
    normalizedMessage.includes('auth')
  );
};

type SonioxClientOptions = {
  /**
   * One of the available Speech-to-Text models.
   */
  model: string;

  // Transcription options
  /**
   * List of language codes to hint the API on what language to transcribe.
   *
   * Example: `['en', 'fr', 'de']`
   */
  languageHints?: string[];

  /** When true, endpoint detection is enabled. */
  enableEndpointDetection?: boolean;

  /**
   * Required for raw PCM formats.
   */
  sampleRate?: number;

  /**
   * Required for raw PCM formats. Typically 1 for mono audio, 2 for stereo.
   */
  numChannels?: number;

  configuration: TranscriptionConfigurationGetter;

  /**
   * Context to pass to the API.
   */
  context?: ContextGetter;

  /**
   * How many messages to queue before websocket is opened. If full, error will be thrown.
   * (opening websocket might take some time, especially if the API key fetching is slow or
   * the user has a slow connection)
   */
  bufferQueueSize?: number;
} & Callbacks;

const getDefaultSonioxClientOptions = (): SonioxClientOptions => ({
  configuration: async () => ({
    apiKey: '',
    websocketUri: defaultWebsocketUri,
  }),
  model: env.VITE_SONIOX_RT_MODEL,
  enableEndpointDetection: false,
  bufferQueueSize: defaultBufferQueueSize,
});

export class SonioxClient {
  private _apiKey: string | null = null;
  private _context: Context | undefined = undefined;
  private _state: RecorderState = 'Init';
  private _options: SonioxClientOptions;
  private _websocket: WebSocket | null;
  private _hasRetriedAuthConfiguration = false;
  private _queuedMessages: (ArrayBuffer | string)[] = []; // Queued data (before websocket is opened)

  /**
   * SonioxClient connects to the Soniox Speech-to-Text API for real-time speech-to-text transcription and translation.
   * It provides a simple API for starting and stopping the transcription, as well as handling the transcription results.
   *
   * @example
   * const sonioxClient = new SonioxClient({
   *   apiKey: '<SONIOX_API_KEY>',
   *   onPartialResult: (result) => {
   *     console.log('partial result', result.text);
   *   },
   * });
   * sonioxClient.start();
   */
  constructor(options?: SonioxClientOptions) {
    this._options = {
      ...getDefaultSonioxClientOptions(),
      ...options,
    };

    this._websocket = null;
  }

  _hasCallback = <T extends keyof Callbacks>(name: T): boolean => {
    return this._options[name] != null;
  };

  _callback = <T extends keyof Callbacks>(
    name: T,
    ...args: Parameters<NonNullable<Callbacks[T]>>
  ): void => {
    // @ts-ignore
    this._options[name]?.(...args);
    // @ts-ignore
    this._audioOptions?.[name]?.(...args);
  };

  _setState(newState: RecorderState): void {
    const oldState = this._state;
    this._state = newState;
    this._callback('onStateChange', {
      oldState,
      newState,
    });
  }

  get state(): RecorderState {
    return this._state;
  }

  /**
   * Start transcription. You can pass options to configure the transcription settings, source and callbacks.
   */
  start = async (): Promise<void> => {
    if (isActiveState(this._state)) {
      throw new Error('SonioxClient is already active');
    }

    this._hasRetriedAuthConfiguration = false;
    this._setState('RequestingMedia');

    // Start collecting data
    this._queuedMessages = [];

    // Add listeners
    await LiveMicrophoneData.addListener(
      'audioData',
      this._onMediaRecorderData,
    );
    // LiveMicrophoneData.addListener('error', this._onMediaRecorderError);
    // LiveMicrophoneData.addListener('pause', this._onMediaRecorderPause);
    // LiveMicrophoneData.addListener('stop', this._onMediaRecorderStop);

    // Start recording
    try {
      await LiveMicrophoneData.startRecording({
        sampleRate: this._options.sampleRate,
        channels: this._options.numChannels,
      });
    } catch (error) {
      this._onError('get_user_media_failed', error?.toString());
    }

    // Fetch configuration (api key and websocket uri)
    let websocketUri: string;
    try {
      const transcriptionConfiguration = await this._options.configuration();
      this._apiKey = transcriptionConfiguration.apiKey;
      websocketUri = transcriptionConfiguration.websocketUri;
    } catch (e) {
      this._onError('api_key_fetch_failed', e?.toString());
      return;
    }

    try {
      this._context = await this._options.context?.();
    } catch (error) {
      console.warn('Could not fetch context', error);
      this._context = undefined;
    }

    // Open websocket
    this._openWebSocket(websocketUri);
  };

  /**
   * Stop transcription. Stopping transcription will send stop signal to the API and wait for the final results to be received.
   * Only after the final results are received, the transcription will be finished. If you want to cancel the transcription immediately,
   * (for example, on component unmount), you should probably use the `cancel()` method instead.
   */
  stop = (): void => {
    if (
      this._state === 'RequestingMedia' ||
      this._state === 'OpeningWebSocket'
    ) {
      this._closeResources();
      this._handleFinished();
    } else if (this._state === 'Running') {
      // Finished recording, waiting for last events from api
      this._setState('FinishingProcessing');
      this._closeSource();
      // Send empty message to api to indicate that we're done
      this._websocket?.send('');
    }
  };

  /**
   * Cancel transcription. Cancelling transcription will stop the transcription immediately and close the resources.
   * For user initiated cancellation, you should probably use the `stop()` method instead.
   */
  cancel = (): void => {
    if (!isInactiveState(this._state)) {
      this._closeResources();
      this._setState('Canceled');
    }
  };

  /**
   * Trigger finalize. This will finalize all non-final tokens.
   */
  finalize = (): void => {
    if (
      this._state === 'RequestingMedia' ||
      this._state === 'OpeningWebSocket'
    ) {
      // Still waiting for websocket to open, queue the event
      if (
        this._queuedMessages.length <
        (this._options.bufferQueueSize ?? defaultBufferQueueSize)
      ) {
        this._queuedMessages.push(finalizeMessage);
      } else {
        this._onError(
          'queue_limit_exceeded',
          'Queue size exceeded before websocket connection was established.',
        );
      }
    } else if (
      this._state === 'Running' ||
      this._state === 'FinishingProcessing'
    ) {
      this._websocket?.send(finalizeMessage);
    }
  };

  private _getAudioPayload = (audioData: AudioData) => {
    // Get audio payload (from web or native)
    let payload: ArrayBuffer;

    const audioDataType = audioData.type;
    switch (audioDataType) {
      case 'web':
        payload = audioData.data;
        break;
      case 'native':
        payload = this.convertFloat32ToPCM16(audioData.data);
        break;
      default: {
        const _exhaustiveCheck: never = audioDataType;
        console.error('Unknown audio data type:', _exhaustiveCheck);
        return null;
      }
    }

    return payload;
  };

  /**
   * Convert Float32 PCM data (normalized -1.0 to 1.0) to PCM s16le (16-bit
   * signed integer little-endian)
   */
  private convertFloat32ToPCM16(float32Data: number[]): ArrayBuffer {
    const buffer = new ArrayBuffer(float32Data.length * 2);
    const view = new DataView(buffer);

    for (let i = 0; i < float32Data.length; i++) {
      // Clamp value to [-1, 1] range
      const sample = Math.max(-1, Math.min(1, float32Data[i]));
      // Convert to 16-bit signed integer
      const int16 = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
      // Write as little-endian
      view.setInt16(i * 2, int16, true);
    }

    return buffer;
  }

  // Media recorder events
  _onMediaRecorderData = async (data: AudioData): Promise<void> => {
    const payload = this._getAudioPayload(data);

    // If payload is null, this is an unsupported platform
    if (payload == null) {
      console.warn('Unsupported audio data platform');
      return;
    }

    if (
      this.state === 'RequestingMedia' ||
      this._state === 'OpeningWebSocket'
    ) {
      // Still waiting for websocket to open, queue the event
      if (
        this._queuedMessages.length <
        (this._options.bufferQueueSize ?? defaultBufferQueueSize)
      ) {
        this._queuedMessages.push(payload);
      } else {
        this._onError(
          'queue_limit_exceeded',
          'Queue size exceeded before websocket connection was established.',
        );
      }
    } else if (this._state === 'Running') {
      this._websocket?.send(payload);
    }

    this._callback('onAudioPayload', payload, data.timestamp);
  };

  _onMediaRecorderError = (event: Event): void => {
    this._onError(
      'media_recorder_error',
      (event as ErrorEvent).error ?? 'Unknown error',
    );
  };

  _onMediaRecorderPause = (_event: Event): void => {
    this.stop();
  };

  _onMediaRecorderStop = (_event: Event): void => {
    this.stop();
  };

  // Websocket events

  _onWebSocketOpen = (event: Event): void => {
    void this._onWebSocketOpenAsync(event);
  };

  _onWebSocketOpenAsync = async (_event: Event): Promise<void> => {
    if (this._state !== 'OpeningWebSocket' || this._options == null) {
      return;
    }

    if (!this._apiKey) {
      this._onError('api_key_fetch_failed', 'API key is missing.');
      return;
    }

    const platformSpecificConfiguration = {
      audio_format: 'pcm_s16le',
      sample_rate: this._options?.sampleRate,
      num_channels: this._options?.numChannels,
    };

    const request: SpeechToTextAPIRequest = {
      api_key: this._apiKey,
      model: this._options.model,
      ...platformSpecificConfiguration,
      language_hints: this._options.languageHints,
      context: this._context,
      enable_speaker_diarization: false,
      enable_language_identification: false,
      enable_endpoint_detection: true,
    };

    // Send initial request
    this._websocket?.send(JSON.stringify(request));

    // Send all queued messages (if any)
    for (const message of this._queuedMessages) {
      this._websocket?.send(message);
    }
    this._queuedMessages = [];

    this._setState('Running');
    this._callback('onStarted');
  };

  _onWebSocketError = (_event: Event): void => {
    if (!isWebSocketState(this._state)) {
      return;
    }
    this._onError('websocket_error', 'WebSocket error occurred.');
  };

  // biome-ignore lint/suspicious/noExplicitAny: It is what it is
  _onWebSocketMessage = (event: MessageEvent<any>): void => {
    if (
      (this._state !== 'Running' && this._state !== 'FinishingProcessing') ||
      this._options == null
    ) {
      return;
    }
    const response = JSON.parse(event.data) as SpeechToTextAPIResponse;

    if (response.error_code != null || response.error_message != null) {
      if (
        !this._hasRetriedAuthConfiguration &&
        isLikelyAuthApiError(response.error_code, response.error_message)
      ) {
        this._hasRetriedAuthConfiguration = true;
        void this._retryWebSocketWithFreshApiKey();
        return;
      }

      this._onError('api_error', response.error_message, response.error_code);
      return;
    }

    this._callback('onPartialResult', response);

    if (response.finished) {
      this._handleFinished();
    }
  };

  private _openWebSocket = (websocketUri: string | undefined): void => {
    this._setState('OpeningWebSocket');
    this._websocket = new WebSocket(websocketUri ?? defaultWebsocketUri);
    this._websocket.addEventListener('open', this._onWebSocketOpen);
    this._websocket.addEventListener('error', this._onWebSocketError);
    this._websocket.addEventListener('message', this._onWebSocketMessage);
  };

  private _retryWebSocketWithFreshApiKey = async (): Promise<void> => {
    if (!isWebSocketState(this._state) || this._options == null) {
      return;
    }

    let websocketUri: string;
    try {
      const transcriptionConfiguration = await this._options.configuration({
        forceRefresh: true,
      });
      this._apiKey = transcriptionConfiguration.apiKey;
      websocketUri = transcriptionConfiguration.websocketUri;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : String(error ?? '');
      this._onError('api_key_fetch_failed', message);
      return;
    }

    this._closeWebSocket();
    this._openWebSocket(websocketUri);
  };

  _onError = (
    status: ErrorStatus,
    message: string | undefined,
    errorCode: number | undefined = undefined,
  ): void => {
    this._setState('Error');
    this._closeResources();

    if (this._hasCallback('onError')) {
      const error = {
        status,
        code: errorCode ?? -1,
        message: message ?? 'Unknown error',
      };
      this._callback('onError', error);
    } else {
      throw new Error(
        `SonioxClient error: ${status}: ${message ?? 'Unknown error'}`,
      );
    }
  };

  _closeSource = async (): Promise<void> => {
    await LiveMicrophoneData.stopRecording();
    await LiveMicrophoneData.removeAllListeners();
  };

  private _closeWebSocket = (): void => {
    if (this._websocket == null) {
      return;
    }

    this._websocket.removeEventListener('open', this._onWebSocketOpen);
    this._websocket.removeEventListener('error', this._onWebSocketError);
    this._websocket.removeEventListener('message', this._onWebSocketMessage);
    this._websocket.close();
    this._websocket = null;
  };

  _closeResources = (): void => {
    this._queuedMessages = [];
    this._closeWebSocket();

    this._closeSource();
  };

  _handleFinished(): void {
    this._closeResources();
    this._setState('Finished');
    this._callback('onFinished');
  }
}

/**
 * @deprecated Use SonioxClient instead.
 *
 */
export const RecordTranscribe = SonioxClient;
